import { useAuth } from "auth/AuthContext";
import { useMemo, useState } from "react";
import { useActionWithPath } from "utils/query";
import { Role, User } from "utils/saastypes";
import { getErrorsMessage } from "utils/utils";
import { ConfirmDelete, RoleModalData, UsersToAdd } from "./types";
import { useAuthMachine } from "auth/state/hook";

export const USER_ROLES: Record<string, string> = {
  COMPANY_USER: "User",
  COMPANY_ROOT: "Root",
  COMPANY_ADMIN: "Admin",
};

export const useUserData = ({
  currentUsers = [],
  refetchAllUsers,
}: {
  currentUsers: User[];
  refetchAllUsers: () => void;
}) => {
  const { authService, setSuccessToast, setErrorToast } = useAuth();
  const [{ currentUser, currentUserRoles, hasGlobalBypass }] =
    useAuthMachine(authService);
  const users = useMemo<User[]>(() => currentUsers, [currentUsers]);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleModal = (val: boolean) => setIsModalOpen(val);

  const [confirmDelete, setConfirmDelete] = useState<ConfirmDelete>({
    confirmDelete: false,
  });
  const [roleModalData, setRoleModalData] = useState<RoleModalData>({
    isOpen: false,
    email: "",
    availableRoles: [],
  });

  const [removeRoleModal, setRemoveRoleModal] = useState({
    isOpen: false,
    email: "",
    roleToBeRemoved: "",
  });

  const [addingUsers, setAddingUsers] = useState(false);
  const addUserAction = useActionWithPath({});
  const deleteUserAction = useActionWithPath({
    onSuccess: () => {
      setSuccessToast(`Successfully removed user from organization.`);
      refetchAllUsers();
    },
  });

  const removeRoleAction = useActionWithPath({
    onSuccess: () => {
      setSuccessToast(`Successfully removed role from user.`);
      refetchAllUsers();
      setRemoveRoleModal({
        isOpen: false,
        roleToBeRemoved: "",
        email: "",
      });
    },
    onError: () => {
      setRemoveRoleModal({
        isOpen: false,
        roleToBeRemoved: "",
        email: "",
      });
    },
  });

  const addRoleAction = useActionWithPath({
    onSuccess: () => {
      setSuccessToast(`Successfully added role to user.`);
      refetchAllUsers();
    },
  });

  const handleAddUsers = async (usersToAdd: UsersToAdd[]) => {
    if (addingUsers) return;
    setAddingUsers(true);
    try {
      const results = await Promise.allSettled(
        usersToAdd.map((user) =>
          // @ts-ignore useActionWithPath accepts a request object at runtime.
          addUserAction.mutateAsync({
            method: "post",
            path: "/users",
            body: JSON.stringify({
              email: user.email.trim(),
              name: user.name?.trim() || undefined,
              role: user.role.id,
            }),
          })
        )
      );
      const succeeded = results.filter(
        (result) => result.status === "fulfilled"
      );
      const failed = results.filter(
        (result): result is PromiseRejectedResult =>
          result.status === "rejected"
      );
      if (succeeded.length) {
        setSuccessToast(
          succeeded.length === 1
            ? "User has access to the organization."
            : `${succeeded.length} users have access to the organization.`
        );
      }
      if (failed.length) {
        const message = await getErrorsMessage(failed[0].reason);
        setErrorToast(
          `${failed.length} user(s) could not be added. ${
            message || "Please try again."
          }`
        );
      } else {
        handleModal(false);
      }
    } catch {
      setErrorToast("Unable to add users. Please try again.");
    } finally {
      setAddingUsers(false);
      refetchAllUsers();
    }
  };

  const handleOpenRoleModal = (val: Role[], email: string) => {
    const availableRoles = Object.keys(USER_ROLES).filter(
      (x: any) => val.indexOf(x) === -1
    );
    setRoleModalData({
      isOpen: true,
      email,
      availableRoles,
    });
  };

  const handleCloseRoleModal = () => {
    setRoleModalData({ isOpen: false, email: "", availableRoles: [] });
  };

  const handleOpenDeleteUserModal = (email: string, userId?: string) => {
    setConfirmDelete({
      confirmDelete: true,
      userEmail: email,
      userId: userId,
    });
  };

  const handleCloseDeleteUserModal = () =>
    setConfirmDelete({ confirmDelete: false });

  const handleAddRole = async (roleName: string, userId: string) => {
    setRoleModalData({ isOpen: false, email: "", availableRoles: [] });
    // @ts-ignore
    addRoleAction.mutate({
      method: "post",
      path: `/users/assignRole?roleName=${roleName}&userId=${encodeURIComponent(
        userId
      )}`,
    });
  };

  const handleDeleteUser = () => {
    // @ts-ignore
    deleteUserAction.mutate({
      method: "delete",
      path: `/users?id=${confirmDelete.userId}`,
    });
    setConfirmDelete({ confirmDelete: false });
  };

  const handleOpenRemoveRoleModal = (role: string, email: string) =>
    setRemoveRoleModal({
      isOpen: true,
      email,
      roleToBeRemoved: role,
    });

  const handleCloseRemoveRoleModal = () => {
    setRemoveRoleModal({ isOpen: false, email: "", roleToBeRemoved: "" });
  };

  const handleRemoveRole = () => {
    const { roleToBeRemoved, email } = removeRoleModal;
    if (roleToBeRemoved && email) {
      // @ts-ignore
      removeRoleAction.mutate({
        method: "post",
        path: `/users/removeRole?roleName=${
          removeRoleModal.roleToBeRemoved
        }&userId=${encodeURIComponent(email)}`,
      });
    }
  };

  return [
    {
      isModalOpen,
      currentUserRoles,
      hasGlobalBypass,
      unifiedData: users,
      addingUsers,
      roleModalData,
      confirmDelete,
      currentUser,
      removeRoleModal,
    },
    {
      handleModal,
      handleAddUsers,
      handleRemoveRole,
      handleOpenRoleModal,
      handleCloseRoleModal,
      handleAddRole,
      handleOpenDeleteUserModal,
      handleCloseDeleteUserModal,
      handleDeleteUser,
      handleCloseRemoveRoleModal,
      handleOpenRemoveRoleModal,
    },
  ] as const;
};
